'use client';

import { useFetchPostById } from '@ps/features/posts/hooks/useFetchPostById';
import { PostChatCard } from '@ps/features/posts/post-card/PostChatCard';
import { bioUrl } from '@ps/functions/bio/bioUrls';

import { postCardDataFromMessageContent } from '../../functions/postCardDataFromMessageContent';
import { useChatContext } from '../../hooks/useChatContext';
import type { ChatPostMessage } from '../../types/ChatMessage';
import { MessageBubbleShell } from './MessageBubbleShell';

interface Props {
  message: ChatPostMessage;
  isOwn: boolean;
}

/**
 * Post bubble — renders the rich `<PostChatMessageView>` from a default model
 * derived off `message.content` (instant), then swaps to the fetched,
 * viewer-scoped post (real media + paywall) once `useFetchPostById` lands. One
 * component for both states — no separate lite fallback.
 */
export function PostMessage({ message, isOwn }: Props) {
  const { bioContext } = useChatContext();
  const { post, loading, error } = useFetchPostById(
    message.content.storeId,
    message.content.postId,
  );
  // A deleted post is dropped from the search index, so its fetch settles with
  // no row — hide the bubble once we KNOW it's gone (after the fetch resolves,
  // not while loading, and not on a transient error, where we keep the snapshot).
  if (!loading && !error && !post) return null;
  const model = post ?? postCardDataFromMessageContent(message.content);
  // `permalink` is `<urlCode>/post/<year>/<month>/<slug>`; the modal needs the
  // bare url code. On a successful unlock, checkout returns to the post's own
  // page: prefer the bio URL builder when this chat carries a `bioContext` (a
  // 1:1 creator / community chat, so the link respects the creator's domain +
  // prefix), and fall back to the hand-made leading-slash permalink on the
  // mixed-creator DM inbox (`bioContext == null`). `redirect_to` is consumed as
  // a hard nav on checkout return, so build the non-intercepting form.
  const { permalink } = message.content;
  const urlCode = permalink.split('/')[0] ?? '';
  const permalinkPath = permalink.split('/').slice(1).join('/');
  const redirectTo =
    bioContext != null && permalinkPath
      ? bioUrl(bioContext).postPermalink(permalinkPath, 'non-intercepting')
      : permalink
        ? `/${permalink}`
        : undefined;

  return (
    <MessageBubbleShell message={message} isOwn={isOwn} bare>
      <PostChatCard
        post={model}
        storeId={message.content.storeId}
        postId={message.content.postId}
        urlCode={urlCode}
        bioContext={bioContext}
        redirectTo={redirectTo}
        isOwn={isOwn}
      />
    </MessageBubbleShell>
  );
}
